import React from 'react';
import { useTranslation } from '@shared/core';
import { EmphasisedValue } from '@shared/components/EmphasisedValue';
import { phraseSlot, withPhraseValues } from '@shared/utils/i18n/phraseValues';

const BALANCE_SLOT = phraseSlot('BALANCE');
const SAVINGS_SLOT = phraseSlot('SAVINGS');
const PERCENT_SLOT = phraseSlot('PERCENT');

type HoneymoonFundBalanceCopyArgs = Readonly<{
  fundBalanceDisplay: string;
  fundCouponSavingsDisplay?: string;
  fundCouponPercentOff?: number;
}>;

// Word for word with the Track Gifts promo (`HoneymoonOffersPromo`) in `yourRegistry`, so every locale's translation is reused.
export const useHoneymoonFundBalanceCopy = ({ fundBalanceDisplay, fundCouponSavingsDisplay, fundCouponPercentOff }: HoneymoonFundBalanceCopyArgs) => {
  const { tq } = useTranslation('yourRegistry');

  const balance = <EmphasisedValue tone="balance">{fundBalanceDisplay}</EmphasisedValue>;
  const body = fundCouponSavingsDisplay
    ? withPhraseValues(
        fundCouponPercentOff
          ? tq`Use your ${BALANCE_SLOT} for a honeymoon or getaway. Save an extra ${SAVINGS_SLOT} (${PERCENT_SLOT}) on rates only for Joy couples.`
          : tq`Use your ${BALANCE_SLOT} for a honeymoon or getaway. Save an extra ${SAVINGS_SLOT} on rates only for Joy couples.`,
        {
          BALANCE: balance,
          SAVINGS: <EmphasisedValue tone="discount">{fundCouponSavingsDisplay}</EmphasisedValue>,
          ...(fundCouponPercentOff && { PERCENT: <EmphasisedValue tone="discount">{`${fundCouponPercentOff}%`}</EmphasisedValue> }),
        },
      )
    : withPhraseValues(tq`Put your ${BALANCE_SLOT} toward a honeymoon or getaway, on rates only for Joy couples.`, { BALANCE: balance });

  return {
    headline: tq`Use your cash fund to book stays`,
    body,
    ctaLabel: tq`Explore stays`,
  };
};
